import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth.guard';
import { LanguagesGuard } from '@core/guards/languages.guard';
import { PermissionEnum } from '@interfaces';
import { LayoutComponent } from '@layout/layout.component';
import { ActivateAccountComponent } from './activate-account/activate-account.component';
import { LoginComponent } from './login/login.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { UserSettingsComponent } from './user-settings/user-settings.component';

import { IsForeignerGuard } from '@core/guards/is-foreigner.guard';
import { I18nLazyloadedResolver } from '@shared/i18n/i18n-lazyloaded-resolver';
import { AccountDeletedComponent } from './account-deleted/account-deleted.component';
import { ChatMobileComponent } from './chat/chat-mobile/chat-mobile.component';
import { NewAccountComponent } from './new-account/new-account.component';
import { NoItemsComponent } from './no-items/no-items.component';
import { UserProcessFormComponent } from './user-process-form/user-process-form.component';
import { InitUserComponent } from './init-user/init-user.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard],
    component: LayoutComponent,
    resolve: { translations: I18nLazyloadedResolver },
    data: { translationsNames: ['auth-shared', 'selects-values'] },
    children: [
      {
        path: 'user-settings',
        component: UserSettingsComponent,
        canActivate: [AuthGuard, LanguagesGuard],
        data: { requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA] },
      },
      {
        path: 'user-process-form',
        component: UserProcessFormComponent,
        canActivate: [AuthGuard],
        data: { requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA] },
      },
      {
        path: 'no-items',
        component: NoItemsComponent,
        canActivate: [AuthGuard],
        data: { requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA] },
      },
      {
        path: 'chat',
        component: ChatMobileComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'user-process',
        loadChildren: () => import('./user-process/user-process.module').then(m => m.UserProcessModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['foreigner'] },
      },
      {
        path: 'management',
        loadChildren: () => import('./management/management.module').then(m => m.ManagementModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['management'] },
      },
      {
        path: 'manager',
        loadChildren: () => import('./manager/manager.module').then(m => m.ManagerModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['manager'] },
      },
      {
        path: 'administration',
        loadChildren: () => import('./administration/administration.module').then(m => m.AdministrationModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['administration', 'management'] },
      },
      {
        path: 'purchase',
        canActivate: [IsForeignerGuard],
        loadChildren: () => import('./purchase/purchase.module').then(m => m.PurchaseModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['purchase'] },
      },
      {
        path: 'partners',
        loadChildren: () => import('./partners/partners.module').then(m => m.PartnersModule),
        canActivate: [AuthGuard],
        resolve: { translations: I18nLazyloadedResolver },
        data: {
          translationsNames: ['management', 'foreigner'],
          requiredPermissions: [PermissionEnum.CAN_MANAGE_USERS_PARTNER_ACCOUNT],
        },
      },
    ],
  },
  {
    path: 'back-office',
    loadChildren: () => import('./back-office/back-office.module').then(m => m.BackOfficeModule),
    resolve: { translations: I18nLazyloadedResolver },
    data: { translationsNames: ['back-office', 'management'] },
  },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'activate-account', component: ActivateAccountComponent },
  { path: 'login', component: LoginComponent },
  { path: 'new-account', component: NewAccountComponent },
  { path: 'account-deleted', component: AccountDeletedComponent },
  { path: 'init-user', component: InitUserComponent },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
